import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import './styles.scss';
import {
  AppForm,
  AppFormField,
  AppFormSubmitButton,
} from '../../../../components/forms';
import { addToWizard, setWizard } from '../../../../store/actions';
import { strings } from '../../../../util/strings';
import { Column, Row } from '../../../../components/layout';
import Header1 from '../../../../components/headers/header1';
import Header2 from '../../../../components/headers/header2';
import RegisterHeader from '../../../../components/headers/register-header';
import * as fonts from '../../../../util/fonts';
import { censorEmail } from '../../../../util/utility';
import { post } from '../../../../api/api';
import ActivateRegistration from '../data-validation/activate-registration';
import { Icons } from '../../../../util/icons';
import colors from '../../../../util/colors';
import NewErrorMessage from '../../../../components/new-error-message';
import { uriCreateUserV2 } from '../../../../api/endpoints';

export interface DataValidationProps {
  onSubmit?(values: any): any;
  setValid?(value: boolean): void;
  nextStep?(): void;
  valid?: boolean;
  wizard: any;
  addToWizard(value: any): any;
  setWizard(value: any): any;
  labels: typeof strings.pt.register;
  messageLabels: typeof strings.pt.messages;
  language?: string;
}

function AccountData({
  valid,
  setValid,
  wizard,
  labels,
  messageLabels,
  onSubmit,
  nextStep,
  language,
}: DataValidationProps) {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [viewRepeatPassword, setViewRepeatPassword] = useState(false);
  const [generalError, setGeneralError] = useState(false);
  const [hoverErrorIcons] = useState<any>({
    password: false,
    repeatPassword: false,
  });

  const handlePasswordIconClick = (repeat: boolean) => {
    if (repeat) {
      setViewRepeatPassword(!viewRepeatPassword);
    } else {
      setViewPassword(!viewPassword);
    }
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(messageLabels?.requiredField)
      .matches(
        /^(?!.* )(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{12,}/,
        labels?.emailAndPassword_passwordError
      ),
    repeatPassword: Yup.string()
      .required(messageLabels?.requiredField)
      .oneOf([Yup.ref('password')], messageLabels.passwordsDontMatch),
  });

  if (!!!wizard?.insuranceValidation) {
    history.push('/');
  }

  const sessionEmail = wizard?.dataValidation?.currentUser?.email || '';
  const isNewEmailFlow = Boolean(
    wizard?.dataValidation?.isNewEmail ||
    wizard?.dataValidation?.newEmail ||
    (wizard?.dataValidation?.selectedContact &&
      sessionEmail &&
      wizard.dataValidation.selectedContact !== sessionEmail)
  );

  const newEmail =
    wizard?.dataValidation?.newEmail ||
    (isNewEmailFlow ? wizard?.dataValidation?.selectedContact : undefined);

  const userEmail =
    newEmail ||
    sessionEmail ||
    wizard?.dataValidation?.currentUser?.email ||
    '';

  // Email a exibir no subtítulo (mascarado)
  const emailToDisplay =
    newEmail ||
    wizard?.dataValidation?.selectedContact ||
    sessionEmail ||
    '';

  const maskedEmail = emailToDisplay.includes('*')
    ? emailToDisplay
    : censorEmail(emailToDisplay);

  // useSessionPhone: Se o fluxo selecionado foi o da validação por telefone, então passa o valor true, caso contrário, então passa false.
  const useSessionPhone = Boolean(
    wizard?.dataValidation?.useSessionPhone ??
    (wizard?.dataValidation?.validationType === 'SMS') ??
    (wizard?.dataValidation?.currentUser?.phone && !wizard?.dataValidation?.useSessionEmail)
  );

  // useSessionEmail: Se o fluxo selecionado foi o da validação por email e o utilizador selecionou que queria o mesmo email, então passa o valor true. Caso contrário, passa false.
  const useSessionEmail = Boolean(
    wizard?.dataValidation?.useSessionEmail ??
    (!isNewEmailFlow && (wizard?.dataValidation?.validationType === 'EMAIL' || !useSessionPhone))
  );

  const sessionId =
    wizard?.dataValidation?.currentUser?.sessionId ||
    wizard?.activateRegistration?.guid ||
    '';

  if (!visible) {
    const formElement = (
      <Column className="account-data-container" style={{ width: '100%', maxWidth: '380px' }}>
        <AppForm
          initialValues={{
            password: '',
            repeatPassword: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { validateForm }) => {
            const errors = await validateForm(values);
            if (Object.keys(errors).length > 0) return;

            setSubmitting(true);
            setGeneralError(false);

            const payload: any = {
              email: userEmail,
              password: values.password,
              registerType: 'MANUAL',
              useSessionEmail: useSessionEmail,
              useSessionPhone: useSessionPhone,
            };

            try {
              const res: any = await post(
                uriCreateUserV2(sessionId),
                JSON.stringify(payload),
                false,
                'Register-AccountData'
              );

              if (
                res?.result?.status === 'Success' ||
                res === true ||
                res?.status === 200 ||
                !res?.error
              ) {
                setVisible(true);
                history.push('/registo-completo');
              } else {
                setGeneralError(true);
                setVisible(false);
              }
            } catch (e: any) {
              setGeneralError(true);
              setVisible(false);
            }

            setSubmitting(false);
          }}>
          <Column style={{ width: '100%' }}>
            {generalError && !submitting && (
              <NewErrorMessage
                iconName={Icons.ALERT_PROBLEM}
                colorIcon={colors.alertRed}
                text={messageLabels?.general_errorMessage}
              />
            )}

            <Row className="account-data-row" style={{ position: 'relative', width: '100%' }}>
              <AppFormField
                showErrorMessage
                name="password"
                type={viewPassword ? 'text' : 'password'}
                passwordIcon
                pwdIconClass={
                  hoverErrorIcons.password
                    ? 'register-pwd-eye-hidden'
                    : 'register-pwd-eye'
                }
                handlePasswordIconClick={() => handlePasswordIconClick(false)}
                showPassword={viewPassword}
                placeholder={labels?.emailAndPassword_passwordPlaceholder}
                style={{ ...fonts.M, width: '100%' }}
              />
            </Row>

            <Row className="account-data-row" style={{ position: 'relative', width: '100%' }}>
              <AppFormField
                showErrorMessage
                name="repeatPassword"
                type={viewRepeatPassword ? 'text' : 'password'}
                passwordIcon
                pwdIconClass={
                  hoverErrorIcons.repeatPassword
                    ? 'register-pwd-eye-hidden'
                    : 'register-pwd-eye'
                }
                handlePasswordIconClick={() => handlePasswordIconClick(true)}
                showPassword={viewRepeatPassword}
                placeholder={labels?.emailAndPassword_confirmPasswordPlaceholder}
                style={{ ...fonts.M, width: '100%' }}
              />
            </Row>

            <Row className="app-center" style={{ marginTop: '1.5rem' }}>
              <AppFormSubmitButton
                title={labels?.insuranceAndPhone_confirmButtonText}
                disabled={submitting}
                className="stepper-continue-button account-data-continue-button"
              />
            </Row>
          </Column>
        </AppForm>
      </Column>
    );

    if (!nextStep) {
      return (
        <Column className="register-main-column">
          <RegisterHeader />
          <Column className="register-container" style={{ alignItems: 'center' }}>
            <Row className="register-header-row" style={{ justifyContent: 'center', marginTop: '2rem' }}>
              <Header1 style={{ ...fonts.XL, ...fonts.Bold, color: colors.black, textAlign: 'center' }}>
                {labels?.register_password_title}
              </Header1>
            </Row>

            <Header2
              style={{
                ...fonts.M,
                textAlign: 'center',
                color: colors.darkGrey,
                marginTop: '0.75rem',
                marginBottom: '2rem',
                maxWidth: '380px',
                lineHeight: '1.4',
              }}
            >
              {labels?.register_password_description}{' '}
              <strong style={{ color: colors.black }}>{maskedEmail}</strong>.
            </Header2>

            {formElement}
          </Column>
        </Column>
      );
    }

    return formElement;
  } else {
    return (
      <ActivateRegistration
        setValid={setValid || (() => {})}
        valid={!!valid}
        onSubmit={onSubmit || (() => {})}
        nextStep={nextStep || (() => {})}
        messageLabels={messageLabels}
      />
    );
  }
}

const mapStateToProps = ({ wizard, stringsDefault }: any) => ({
  wizard: wizard.value,
  labels: stringsDefault.value.pt.register,
  messageLabels: stringsDefault.value.pt.messages,
});

const mapDispatchToProps = (dispatch: any) => ({
  setWizard: (value: any) => dispatch(setWizard(value)),
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(AccountData)
);
